/**
 * Device identity.
 *
 * WHY THIS IS A GENERATED ID AND NOT A DERIVED ONE
 * -------------------------------------------------
 * The obvious implementations are all wrong, in different ways:
 *
 *  - a browser tab id dies with the tab, so the "same device" is a new device on
 *    every refresh, and the cloud accumulates thousands of phantom devices that
 *    each pin the change log forever;
 *  - `navigator.userAgent` is not unique — two phones on the same OS are
 *    identical, and one phone after an OS update is a different id;
 *  - a MAC address or a hardware fingerprint would work, and is also an
 *    unacceptable privacy trade for a shop that runs its billing on a borrowed
 *    laptop.
 *
 * So: a ULID generated ONCE and stored in IndexedDB. It survives restarts, is
 * unique without coordination, and says nothing about the machine.
 *
 * WHY IT IS NOT THE BUSINESS ID
 * -----------------------------
 * Because one machine runs more than one business — a consultant with two shops,
 * or an accountant testing on a copy of a client's books. Scoping sync to the
 * business alone would have those two sets of books share a cursor, and one
 * would read the other's invoices.
 *
 * So there are TWO identities and they answer different questions:
 *
 *   the device id   ONE per install. "Is this the same machine as last week?"
 *   the devices row ONE per business. "What is this business's view of a device?"
 *
 * The device id lives in `settings` so it has exactly one home and cannot drift;
 * the row exists so a device can be labelled, listed and revoked per business.
 */
import type { DeviceRecord, Setting } from '@data/db/types';
import { newId } from '@platform/ids';
import type { DNRDatabase } from '@data/db/database';

/** Where the install's device id lives. See the file comment. */
export const DEVICE_ID_SETTING_ID = 'app:deviceId';

/** The row id is `${businessId}:${deviceId}`, so one device is one row per business. */
function rowId(businessId: string, deviceId: string): string {
  return `${businessId}:${deviceId}`;
}

/**
 * This install's device id, generated once and reused forever after.
 *
 * Not a tab id (dies on refresh), not a user agent (two identical phones
 * collide, and an OS update changes it), not a hardware fingerprint (works, and
 * is an unacceptable privacy trade for a shop running its books on a borrowed
 * laptop). A stored ULID says nothing about the machine, needs no coordination,
 * and survives restarts.
 *
 * WHY THE WRITE IS DONE IN ITS OWN TRANSACTION
 * --------------------------------------------
 * The row has to exist before it can be read, and a business service must not
 * carry a `settings` write inside its own transaction: the device row is not part
 * of that business operation, and folding it in would mean every invoice
 * transaction also spans `settings`. A separate, immediate transaction keeps the
 * two concerns apart, and it runs exactly once per install.
 */
export async function currentDeviceId(db: DNRDatabase): Promise<string> {
  const existing = await db.settings.get(DEVICE_ID_SETTING_ID);
  const value = (existing?.value ?? '').toString();
  if (value) return value;
  const deviceId = newId();
  const setting: Setting = {
    id: DEVICE_ID_SETTING_ID,
    scope: 'device',
    scopeId: 'app',
    key: DEVICE_ID_SETTING_ID,
    value: deviceId,
    updatedAt: new Date().toISOString(),
  };
  await db.transaction('rw', db.settings, () => db.settings.put(setting));
  return deviceId;
}

/**
 * Get this device's row for a business, creating it on first sync.
 *
 * Lazy on purpose: there is no startup work worth doing, and a user who never
 * syncs never gets a device row at all.
 */
export async function ensureDevice(
  db: DNRDatabase,
  businessId: string,
  now = new Date().toISOString(),
): Promise<DeviceRecord> {
  const deviceId = await currentDeviceId(db);
  const id = rowId(businessId, deviceId);
  const existing = await db.devices.get(id);
  if (existing) {
    const updated: DeviceRecord = { ...existing, lastSeenAt: now };
    await db.devices.put(updated);
    return updated;
  }
  const record: DeviceRecord = {
    id,
    businessId,
    platform: coarsePlatform(),
    firstSeenAt: now,
    lastSeenAt: now,
  };
  await db.devices.put(record);
  return record;
}

/**
 * Re-adopt an existing device id.
 *
 * Used after a restore onto a machine that should inherit this device's
 * position, so the row matches the `app:deviceId` setting rather than the two
 * disagreeing — a mismatch would mean two cursors and two sync histories for one
 * physical machine.
 *
 * No business data is touched and no change is enqueued. The point is purely to
 * keep one logical device, so its acknowledgement stops pinning the log.
 */
export async function registerKnownDevice(
  db: DNRDatabase,
  businessId: string,
  deviceId: string,
  now = new Date().toISOString(),
): Promise<DeviceRecord> {
  const id = rowId(businessId, deviceId);
  const existing = await db.devices.get(id);
  const record: DeviceRecord = existing
    ? { ...existing, lastSeenAt: now }
    : { id, businessId, platform: coarsePlatform(), firstSeenAt: now, lastSeenAt: now };
  await db.devices.put(record);
  return record;
}

/**
 * Stop trusting a device.
 *
 * Revocation removes the local row only. The provider is told separately, and a
 * revoked device that is merely forgotten is not a security control — a revoked
 * device still holds a cursor, so the cloud side has to be told as well.
 */
export async function revokeDevice(
  db: DNRDatabase,
  businessId: string,
  deviceId: string,
  now = new Date().toISOString(),
): Promise<void> {
  const id = rowId(businessId, deviceId);
  const existing = await db.devices.get(id);
  if (!existing) return;
  await db.devices.put({ ...existing, revokedAt: now, lastSeenAt: now });
}

/**
 * Reset the global device id, forcing a new one to be generated on next use.
 * Used when the current device has been revoked and needs to re-register.
 */
export async function resetDeviceId(db: DNRDatabase): Promise<string> {
  await db.settings.delete(DEVICE_ID_SETTING_ID);
  return currentDeviceId(db);
}

/** Touch lastSeenAt. Cheap, and not worth a transaction of its own. */
export async function touchDevice(
  db: DNRDatabase,
  businessId: string,
  now = new Date().toISOString(),
): Promise<void> {
  const deviceId = await currentDeviceId(db);
  const id = rowId(businessId, deviceId);
  const existing = await db.devices.get(id);
  if (existing) await db.devices.put({ ...existing, lastSeenAt: now });
}

/**
 * A coarse platform word for the settings list.
 *
 * Deliberately three buckets. A precise fingerprint would identify a machine far
 * better than this feature needs, and a settings screen that says "Windows 11
 * build 22631" is not more useful than one that says "Desktop".
 */
function coarsePlatform(): string {
  const nav = globalThis.navigator as { userAgent?: string; userAgentData?: { platform?: string } } | undefined;
  const hint = (nav?.userAgentData?.platform || nav?.userAgent || '').toLowerCase();
  if (hint.includes('android')) return 'android';
  if (hint.includes('iphone') || hint.includes('ipad')) return 'ios';
  if (hint.includes('win')) return 'windows';
  if (hint.includes('mac')) return 'macos';
  if (hint.includes('linux')) return 'linux';
  return 'unknown';
}
